import React, { useState, useEffect } from 'react';
import { FilterButton } from '../components/UI/FilterButton';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import PillToggle from '../components/UI/PillToggle';
import { 
  Building2, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  BarChart3,
  UserPlus,
  X,
  Grid3X3,
  List,
} from 'lucide-react';
import api from '../services/api';
import { formatDate, formatDateTime, formatRelativeTime } from '../utils/dateUtils';
import Modal from '../components/UI/Modal';
import ConfirmModal from '../components/UI/ConfirmModal';
import { getDepartmentIcon, getDepartmentColor } from '../config/departmentConfig';

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'chart'
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [employeeStats, setEmployeeStats] = useState({
    total: 0,
    newHires: 0,
    probation: 0,
    avgTenure: 0,
    departments: 0,
    systemAccounts: 0,
    totalIncludingInactive: 0
  });

  const [formData, setFormData] = useState({
    department_name: '',
    description: '',
    head_of_department: [], // Changed to array for multiple HODs
    deputy_head_of_department: [], // Added for Deputy HODs
    lead_trade_leader: [] // Lead Trade Leaders (above Trade Leaders, below Deputy HOD/HOD)
  });

  // Department icon and color are resolved from the centralized config
  // in ../config/departmentConfig.js (getDepartmentIcon / getDepartmentColor)

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchDepartments(), fetchEmployees(), fetchEmployeeStats()]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics when departments or employees change
  useEffect(() => {
    if (departments.length > 0 && employees.length > 0 && employeeStats.total > 0) {
      calculateAnalytics();
    }
  }, [departments, employees, employeeStats.total]);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/configuration/departments/');
      const departmentData = response.data.results || response.data || [];
      setDepartments(departmentData);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Fetch ALL employees by making multiple requests if needed
      let allEmployees = [];
      let page = 1;
      let hasMorePages = true;
      
      while (hasMorePages) {
        const requestParams = { 
          page: page,
          page_size: 100,
          employment_status: 'active'
        };
        
        const response = await api.get('/staff/', {
          params: requestParams
        });
        
        const results = response.data.results || response.data || [];
        allEmployees = [...allEmployees, ...results];
        
        // Check if there are more pages
        hasMorePages = response.data.next !== null;
        page++;
        
        // Safety check to prevent infinite loop
        if (page > 50) {
          console.warn('Too many pages, stopping fetch');
          break;
        }
      }
      
      setEmployees(allEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchEmployeeStats = async () => {
    try {
      // Use the dedicated statistics endpoint for accurate data
      const response = await api.get('/staff/statistics/');
      const statsData = response.data;
      
      // Map the API response to our component state
      const stats = {
        total: statsData.total_staff || 0,  // Only active, non-system employees
        newHires: statsData.new_hires_this_year || 0,  // New hires this year
        probation: statsData.probation_staff || 0,  // Employees on probation
        avgTenure: statsData.avg_tenure_years || 0,  // Average tenure in years
        departments: statsData.unique_departments || 0,  // Number of unique departments
        systemAccounts: statsData.system_accounts || 0,  // System accounts excluded from total
        totalIncludingInactive: statsData.total_including_inactive || 0  // Total including inactive
      };
      
      setEmployeeStats(stats);
      
      console.log('📊 Department Management Stats:', {
        total: stats.total,
        newHires: stats.newHires,
        probation: stats.probation,
        avgTenure: stats.avgTenure,
        departments: stats.departments,
        systemAccounts: stats.systemAccounts,
        totalIncludingInactive: stats.totalIncludingInactive
      });
      
    } catch (error) {
      console.error('Error fetching employee stats:', error);
      // Fallback to manual calculation if statistics endpoint fails
      setEmployeeStats({
        total: 0,
        newHires: 0,
        probation: 0,
        avgTenure: 0,
        departments: 0,
        systemAccounts: 0,
        totalIncludingInactive: 0
      });
    }
  };

  const calculateAnalytics = () => {
    try {
      if (!departments || departments.length === 0 || !employees || employees.length === 0) {
        return;
      }
      
      const departmentAnalytics = {};
      
      departments.forEach(dept => {
        const deptEmployees = employees.filter(emp => {
          // Handle different department field formats - match by name (same logic as getDepartmentEmployees)
          if (emp.department?.id) {
            // Department is an object with id and department_name
            return emp.department.department_name === dept.department_name;
          } else if (emp.department?.department_name) {
            // Department is an object with department_name only
            return emp.department.department_name === dept.department_name;
          } else if (typeof emp.department === 'string') {
            // Department is a string (department_name)
            return emp.department === dept.department_name;
          }
          return false;
        });
        
        // Use same filtering logic as backend statistics endpoint
        const nonSystemEmployees = deptEmployees.filter(emp => 
                  emp.is_active &&
        emp.employment_status?.toLowerCase() === 'active' &&
          ['managing_director', 'executive_director', 'staff', 'worker', 'intern'].includes(emp.employment_classification)
        );
        
        departmentAnalytics[dept.id] = {
          totalEmployees: nonSystemEmployees.length,
          activeEmployees: nonSystemEmployees.filter(emp => emp.is_active).length,
          recentHires: nonSystemEmployees.filter(emp => {
            if (!emp.employment_start_date) return false;
            const startDate = new Date(emp.employment_start_date);
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            return startDate >= threeMonthsAgo;
          }).length,
          avgTenure: calculateAverageTenure(nonSystemEmployees)
        };
      });
      
      setAnalytics(departmentAnalytics);
    } catch (error) {
      console.error('Error calculating analytics:', error);
    }
  };

  const calculateAverageTenure = (employees) => {
    if (employees.length === 0) return { years: 0, months: 0, display: '0m' };
    
    const totalMonths = employees.reduce((sum, emp) => {
      if (!emp.employment_start_date) return sum;
      const startDate = new Date(emp.employment_start_date);
      const now = new Date();
      const months = (now.getFullYear() - startDate.getFullYear()) * 12 + 
                    (now.getMonth() - startDate.getMonth());
      return sum + Math.max(0, months);
    }, 0);
    
    const avgTotalMonths = Math.round(totalMonths / employees.length);
    const years = Math.floor(avgTotalMonths / 12);
    const months = avgTotalMonths % 12;
    
    // Create display string
    let display = '';
    if (years > 0 && months > 0) {
      display = `${years}y ${months}m`;
    } else if (years > 0) {
      display = `${years}y`;
    } else {
      display = `${months}m`;
    }
    
    return { years, months, display };
  };

  const getDepartmentEmployees = (deptName) => {
    return employees.filter(emp => {
      // Use same filtering logic as backend statistics endpoint
            if (!emp.is_active ||
          emp.employment_status?.toLowerCase() !== 'active' ||
          !['managing_director', 'executive_director', 'staff', 'worker', 'intern'].includes(emp.employment_classification)) {
        return false;
      }
      
      // Handle different department field formats
      if (emp.department?.id) {
        // Department is an object with id and department_name
        return emp.department.department_name === deptName;
      } else if (emp.department?.department_name) {
        // Department is an object with department_name only
        return emp.department.department_name === deptName;
      } else if (typeof emp.department === 'string') {
        // Department is a string (department_name)
        return emp.department === deptName;
      }
      return false;
    });
  };

  // Function to get filtered employees for HOD selection
  const getFilteredEmployeesForHOD = (departmentName) => {
    return employees.filter(emp => {
      // Include employees from the current department (if editing)
      if (departmentName) {
        // Check if employee belongs to the current department
        if (emp.department?.department_name === departmentName || 
            (typeof emp.department === 'string' && emp.department === departmentName)) {
          return true;
        }
      }
      
      // Include employees with no department assigned (null, undefined, empty string, or empty object)
      const hasNoDepartment = !emp.department || 
          emp.department === null || 
          emp.department === undefined ||
          emp.department === '' ||
          (typeof emp.department === 'object' && Object.keys(emp.department).length === 0) ||
          (typeof emp.department === 'object' && (!emp.department.id && !emp.department.department_name));
      
      if (hasNoDepartment) {
        return true;
      }
      
      return false;
    });
  };

  // Function to get employee name by staff_id
  const getEmployeeName = (staffId) => {
    const employee = employees.find(emp => emp.staff_id === staffId);
    return employee ? `${employee.first_name} ${employee.last_name}` : staffId;
  };

  // Function to handle multiple HOD selection
  const handleHODSelection = (staffId) => {
    const newHODs = formData.head_of_department.includes(staffId)
      ? formData.head_of_department.filter(id => id !== staffId)
      : [...formData.head_of_department, staffId];
    setFormData({...formData, head_of_department: newHODs});
  };

  // Function to handle Deputy HOD selection
  const handleDeputyHODSelection = (staffId) => {
    const newDeputyHODs = formData.deputy_head_of_department.includes(staffId)
      ? formData.deputy_head_of_department.filter(id => id !== staffId)
      : [...formData.deputy_head_of_department, staffId];
    setFormData({...formData, deputy_head_of_department: newDeputyHODs});
  };

  // Function to handle Lead Trade Leader selection
  const handleLeadTradeLeaderSelection = (staffId) => {
    const newLeads = formData.lead_trade_leader.includes(staffId)
      ? formData.lead_trade_leader.filter(id => id !== staffId)
      : [...formData.lead_trade_leader, staffId];
    setFormData({...formData, lead_trade_leader: newLeads});
  };

  const handleAddDepartment = () => {
    setEditingDepartment(null);
    setFormData({
      department_name: '',
      description: '',
      head_of_department: [], // Changed to array
      deputy_head_of_department: [], // Added for Deputy HODs
      lead_trade_leader: [] // Lead Trade Leaders
    });
    setShowDepartmentForm(true);
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setFormData({
      department_name: department.department_name || '',
      description: department.description || '',
      head_of_department: Array.isArray(department.head_of_department) 
        ? department.head_of_department 
        : [], // Backend now returns array of staff_ids
      deputy_head_of_department: Array.isArray(department.deputy_head_of_department) 
        ? department.deputy_head_of_department 
        : [], // Backend now returns array of staff_ids
      lead_trade_leader: Array.isArray(department.lead_trade_leader)
        ? department.lead_trade_leader
        : [] // Backend now returns array of staff_ids
    });
    setShowDepartmentForm(true);
  };

  const handleSubmitDepartment = async (e) => {
    e.preventDefault();
    try {
      if (editingDepartment) {
        const response = await api.put(`/configuration/departments/${editingDepartment.id}/`, formData);
        setDepartments(departments.map(dept => 
          dept.id === editingDepartment.id ? response.data : dept
        ));
      } else {
        const response = await api.post('/configuration/departments/', formData);
        setDepartments([...departments, response.data]);
      }
      
      setShowDepartmentForm(false);
      setEditingDepartment(null);
      // Analytics will be recalculated automatically via useEffect
    } catch (error) {
      console.error('Error saving department:', error);
      alert('Error saving department. Please try again.');
    }
  };

  const handleDeleteDepartment = (departmentId) => {
    setConfirmAction({ departmentId });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await api.delete(`/configuration/departments/${confirmAction.departmentId}/`);
      setDepartments(departments.filter(dept => dept.id !== confirmAction.departmentId));
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('Error deleting department. Please try again.');
    } finally {
      setConfirmAction(null);
    }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.department_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (dept.description && dept.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalDepartments = departments.length;
  
  // Calculate employees with departments assigned
  const employeesWithDepartments = employees.filter(emp => {
    return emp.department && (
      emp.department.id || 
      emp.department.department_name || 
      (typeof emp.department === 'string' && emp.department !== '')
    );
  }).length;



  const renderStatsCards = () => {
    // Use backend statistics for accurate metrics
    const avgDepartmentSize = totalDepartments > 0 ? Math.round(employeeStats.total / totalDepartments) : 0;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{totalDepartments}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Total Departments</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{employeeStats.total}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Active Employees</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{avgDepartmentSize}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Avg Dept Size</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{employeeStats.newHires}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Recent Hires (1yr)</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGridCard = (department) => {
    const deptAnalytics = analytics[department.id] || {};
    const IconComponent = getDepartmentIcon(department.department_name);
    const iconColors = getDepartmentColor(department.department_name);
    const departmentHODs = Array.isArray(department.head_of_department) ? department.head_of_department : [];
    const departmentDeputyHODs = Array.isArray(department.deputy_head_of_department) ? department.deputy_head_of_department : [];

    return (
      <div key={department.id} className="bg-white rounded-2xl shadow-sm border p-4 hover:shadow-md transition-shadow flex flex-col h-full">
        {/* Header: icon + name + active badge */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 ${iconColors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
              <IconComponent className={`w-5 h-5 ${iconColors.text}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{department.department_name}</h3>
              {department.description && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{department.description}</p>
              )}
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
            {deptAnalytics.activeEmployees || 0} active
          </span>
        </div>

        {/* HOD */}
        {departmentHODs.length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-medium text-gray-500 mb-1">Head of Department</p>
            <div className="flex flex-wrap gap-1.5">
              {departmentHODs.map(hodId => (
                <span key={hodId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                  {getEmployeeName(hodId)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Deputy HOD */}
        {departmentDeputyHODs.length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-medium text-gray-500 mb-1">Deputy Head</p>
            <div className="flex flex-wrap gap-1.5">
              {departmentDeputyHODs.map(id => (
                <span key={id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                  {getEmployeeName(id)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 py-3 mt-2 border-t border-gray-100">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900">{deptAnalytics.activeEmployees || 0}</p>
            <p className="text-xs text-gray-500">Active</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900">{deptAnalytics.recentHires || 0}</p>
            <p className="text-xs text-gray-500">Recent Hires</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900">{deptAnalytics.avgTenure?.display || '0m'}</p>
            <p className="text-xs text-gray-500">Avg Tenure</p>
          </div>
        </div>

        {/* Actions */}
        <div className="card-action-footer justify-start">
          <button
            onClick={() => handleEditDepartment(department)}
            className="btn btn-admin-outline btn-sm text-xs"
          >
            <Edit className="w-3.5 h-3.5 mr-1" />
            Edit
          </button>
          <button
            onClick={() => setSelectedDepartment(department)}
            className="btn btn-admin-outline btn-sm text-xs"
          >
            <Eye className="w-3.5 h-3.5 mr-1" />
            View Details
          </button>
          <div className="flex-1" />
          <button
            onClick={() => handleDeleteDepartment(department.id)}
            className="icon-button-danger"
            title="Delete department"
            aria-label="Delete department"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="bg-white rounded-2xl shadow-sm border overflow-clip">
        {/* Mobile card view */}
        <div className="md:hidden p-4 space-y-3">
          {filteredDepartments.map(department => {
            const deptAnalytics = analytics[department.id] || {};
            const IconComponent = getDepartmentIcon(department.department_name);
            const iconColors = getDepartmentColor(department.department_name);
            const departmentHODs = Array.isArray(department.head_of_department) ? department.head_of_department : [];
            const departmentDeputyHODs = Array.isArray(department.deputy_head_of_department) ? department.deputy_head_of_department : [];

            return (
              <div key={department.id} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 ${iconColors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className={`w-4 h-4 ${iconColors.text}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{department.department_name}</p>
                      {department.description && (
                        <p className="text-xs text-gray-500 truncate">{department.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {deptAnalytics.activeEmployees || 0} active
                    </span>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Head of Dept</span>
                    {departmentHODs.length > 0 ? (
                      <span className="flex flex-wrap justify-end gap-1.5 min-w-0">
                        {departmentHODs.map(hodId => (
                          <span key={hodId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {getEmployeeName(hodId)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not assigned</span>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Deputy Head</span>
                    {departmentDeputyHODs.length > 0 ? (
                      <span className="flex flex-wrap justify-end gap-1.5 min-w-0">
                        {departmentDeputyHODs.map(id => (
                          <span key={id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            {getEmployeeName(id)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not assigned</span>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Active</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{deptAnalytics.activeEmployees || 0}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Recent Hires</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{deptAnalytics.recentHires || 0}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Avg Tenure</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{deptAnalytics.avgTenure?.display || '0m'}</span>
                  </div>
                </div>
                <div className="card-action-footer">
                  <button
                    onClick={() => handleEditDepartment(department)}
                    className="icon-button-admin"
                    title="Edit department"
                    aria-label="Edit department"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setSelectedDepartment(department)}
                    className="icon-button-admin"
                    title="View details"
                    aria-label="View details"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteDepartment(department.id)}
                    className="icon-button-danger"
                    title="Delete department"
                    aria-label="Delete department"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table view */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Head of Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Deputy Head</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Active</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Recent Hires</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Avg Tenure</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredDepartments.map(department => {
                const deptAnalytics = analytics[department.id] || {};
                const IconComponent = getDepartmentIcon(department.department_name);
                const iconColors = getDepartmentColor(department.department_name);
                const departmentHODs = Array.isArray(department.head_of_department) ? department.head_of_department : [];
                const departmentDeputyHODs = Array.isArray(department.deputy_head_of_department) ? department.deputy_head_of_department : [];

                return (
                  <tr key={department.id} className="table-row">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 ${iconColors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <IconComponent className={`w-4 h-4 ${iconColors.text}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{department.department_name}</p>
                          {department.description && (
                            <p className="text-xs text-gray-500 truncate max-w-[200px]">{department.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      {departmentHODs.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {departmentHODs.map(hodId => (
                            <span key={hodId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              {getEmployeeName(hodId)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {departmentDeputyHODs.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {departmentDeputyHODs.map(id => (
                            <span key={id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                              {getEmployeeName(id)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className="text-sm font-semibold text-gray-900">{deptAnalytics.activeEmployees || 0}</span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className="text-sm text-gray-900">{deptAnalytics.recentHires || 0}</span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className="text-sm text-gray-900">{deptAnalytics.avgTenure?.display || '0m'}</span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEditDepartment(department)}
                          className="icon-button-admin"
                          title="Edit department"
                          aria-label="Edit department"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setSelectedDepartment(department)}
                          className="icon-button-admin"
                          title="View details"
                          aria-label="View details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteDepartment(department.id)}
                          className="icon-button-danger"
                          title="Delete department"
                          aria-label="Delete department"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Department Management</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Organizational structure, analytics, and department oversight</p>
        </div>
        <button
          onClick={handleAddDepartment}
          className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Department</span>
        </button>
      </div>



      {/* Stats Cards */}
      {renderStatsCards()}

      {/* Search and View Controls — single row at every breakpoint: the
          icon-only filter button and the view PillToggle stay grouped inline
          on the right, instead of the toggle dropping to its own mobile row. */}
      <div className="bg-white rounded-2xl shadow-sm border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 max-w-xl min-w-0">
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search departments..."
              theme="admin"
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              onClick={() => setShowFilters(v => !v)}
            />
            <PillToggle
              value={viewMode}
              onChange={setViewMode}
              options={[
                { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                { id: 'list', label: 'List view', icon: List },
              ]}
              variant="icon"
              theme="admin"
              ariaLabel="View mode"
            />
          </div>
        </div>
      </div>

      {/* Departments Grid/List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4">
              <div className="animate-pulse space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center space-y-1">
                    <div className="h-5 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                  <div className="text-center space-y-1">
                    <div className="h-5 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                  <div className="text-center space-y-1">
                    <div className="h-5 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDepartments.map(renderGridCard)}
        </div>
      ) : (
        renderListView()
      )}

      {/* Empty State */}
      {!loading && filteredDepartments.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {searchTerm ? 'No departments found' : 'No departments yet'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchTerm 
              ? 'Try adjusting your search terms.' 
              : 'Get started by creating your first department.'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={handleAddDepartment}
              className="mt-4 btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Department
            </button>
          )}
        </div>
      )}

      {/* Department Form Modal */}
      <Modal
        isOpen={showDepartmentForm}
        onClose={() => setShowDepartmentForm(false)}
        title={editingDepartment ? 'Edit Department' : 'Add New Department'}
        size="lg"
        theme="admin"
        footer={
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowDepartmentForm(false)}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="department-form"
              className="btn btn-primary"
            >
              {editingDepartment ? 'Update Department' : 'Create Department'}
            </button>
          </div>
        }
      >
        <form id="department-form" onSubmit={handleSubmitDepartment} className="space-y-6">
              <div className="form-group">
                <label className="form-label">
                  Department Name *
                </label>
                <input
                  type="text"
                  value={formData.department_name}
                  onChange={(e) => setFormData({...formData, department_name: e.target.value})}
                  className="input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="textarea"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Head of Department
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select one or more employees to be heads of this department. 
                  Only employees from this department or unassigned employees are shown.
                </p>
                
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-xl">
                  {getFilteredEmployeesForHOD(editingDepartment?.department_name).length > 0 ? (
                    getFilteredEmployeesForHOD(editingDepartment?.department_name).map(emp => (
                      <label key={emp.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={formData.head_of_department.includes(emp.staff_id)}
                          onChange={() => handleHODSelection(emp.staff_id)}
                          className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus-visible:ring-blue-500 focus-visible:ring-2"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600">
                                {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {emp.staff_id} • {emp.department?.department_name || 'No Department'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No eligible employees found</p>
                      <p className="text-xs mt-1">
                        {editingDepartment 
                          ? 'No employees in this department or unassigned employees available'
                          : 'Add department employees first or ensure there are unassigned employees'
                        }
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Selected HODs Display */}
                {formData.head_of_department.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Selected HODs:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.head_of_department.map(hodId => (
                        <span 
                          key={hodId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {getEmployeeName(hodId)}
                          <button
                            type="button"
                            onClick={() => handleHODSelection(hodId)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Deputy Head of Department
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select one or more employees to be deputy heads of this department. 
                  Only employees from this department or unassigned employees are shown.
                </p>
                
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-xl">
                  {getFilteredEmployeesForHOD(editingDepartment?.department_name).length > 0 ? (
                    getFilteredEmployeesForHOD(editingDepartment?.department_name).map(emp => (
                      <label key={emp.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={formData.deputy_head_of_department.includes(emp.staff_id)}
                          onChange={() => handleDeputyHODSelection(emp.staff_id)}
                          className="w-4 h-4 text-green-600 bg-white border-gray-300 rounded focus-visible:ring-green-500 focus-visible:ring-2"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-green-600">
                                {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {emp.staff_id} • {emp.department?.department_name || 'No Department'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No eligible employees found</p>
                      <p className="text-xs mt-1">
                        {editingDepartment 
                          ? 'No employees in this department or unassigned employees available'
                          : 'Add department employees first or ensure there are unassigned employees'
                        }
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Selected Deputy HODs Display */}
                {formData.deputy_head_of_department.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Selected Deputy HODs:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.deputy_head_of_department.map(deputyHodId => (
                        <span 
                          key={deputyHodId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          {getEmployeeName(deputyHodId)}
                          <button
                            type="button"
                            onClick={() => handleDeputyHODSelection(deputyHodId)}
                            className="ml-2 text-green-600 hover:text-green-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Lead Trade Leader
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select one or more employees to be Lead Trade Leaders — they sit
                  above the individual Trade Leaders but below the Deputy HOD/HOD,
                  and get department-wide team monitoring (OT &amp; rest day).
                  Only employees from this department or unassigned employees are shown.
                </p>

                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-xl">
                  {getFilteredEmployeesForHOD(editingDepartment?.department_name).length > 0 ? (
                    getFilteredEmployeesForHOD(editingDepartment?.department_name).map(emp => (
                      <label key={emp.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={formData.lead_trade_leader.includes(emp.staff_id)}
                          onChange={() => handleLeadTradeLeaderSelection(emp.staff_id)}
                          className="w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus-visible:ring-purple-500 focus-visible:ring-2"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-purple-600">
                                {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {emp.staff_id} • {emp.department?.department_name || 'No Department'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No eligible employees found</p>
                      <p className="text-xs mt-1">
                        {editingDepartment 
                          ? 'No employees in this department or unassigned employees available'
                          : 'Add department employees first or ensure there are unassigned employees'
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Selected Lead Trade Leaders Display */}
                {formData.lead_trade_leader.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Selected Lead Trade Leaders:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.lead_trade_leader.map(leadId => (
                        <span 
                          key={leadId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          {getEmployeeName(leadId)}
                          <button
                            type="button"
                            onClick={() => handleLeadTradeLeaderSelection(leadId)}
                            className="ml-2 text-purple-600 hover:text-purple-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </form>
      </Modal>

      {/* Department Details Modal */}
      <Modal
        isOpen={!!selectedDepartment}
        onClose={() => setSelectedDepartment(null)}
        title={selectedDepartment?.department_name}
        size="xl"
        theme="admin"
        customHeader={selectedDepartment && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center space-x-3">
              {(() => {
                const IconComponent = getDepartmentIcon(selectedDepartment.department_name);
                const iconColors = getDepartmentColor(selectedDepartment.department_name);
                return (
                  <div className={`w-10 h-10 ${iconColors.bg} rounded-full flex items-center justify-center`}>
                    <IconComponent className={`w-5 h-5 ${iconColors.text}`} />
                  </div>
                );
              })()}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedDepartment.department_name}
                </h2>
                <p className="text-sm text-gray-600">Department Details</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedDepartment(null)}
              className="icon-button-admin"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        footer={
          <div className="flex justify-end">
            <button
              onClick={() => setSelectedDepartment(null)}
              className="btn btn-admin-outline"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedDepartment && (
          <div className="p-6 space-y-6">
                {/* Department Information */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    Department Information
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <div>
                      <span className="text-xs font-medium text-gray-500">Department Name</span>
                      <p className="text-sm text-gray-900 mt-1">{selectedDepartment.department_name}</p>
                    </div>
                    {selectedDepartment.description && (
                      <div>
                        <span className="text-xs font-medium text-gray-500">Description</span>
                        <p className="text-sm text-gray-900 mt-1">{selectedDepartment.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Heads of Department */}
                {(() => {
                  const departmentHODs = Array.isArray(selectedDepartment.head_of_department) 
                    ? selectedDepartment.head_of_department 
                    : [];
                  
                  if (departmentHODs.length > 0) {
                    return (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          {departmentHODs.length === 1 ? 'Head of Department' : 'Heads of Department'}
                        </h3>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {departmentHODs.map(hodId => (
                              <span 
                                key={hodId}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {getEmployeeName(hodId)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Deputy Heads of Department */}
                {(() => {
                  const departmentDeputyHODs = Array.isArray(selectedDepartment.deputy_head_of_department) 
                    ? selectedDepartment.deputy_head_of_department 
                    : [];
                  
                  if (departmentDeputyHODs.length > 0) {
                    return (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          {departmentDeputyHODs.length === 1 ? 'Deputy Head of Department' : 'Deputy Heads of Department'}
                        </h3>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {departmentDeputyHODs.map(deputyHodId => (
                              <span 
                                key={deputyHodId}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                {getEmployeeName(deputyHodId)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Lead Trade Leaders */}
                {(() => {
                  const departmentLeads = Array.isArray(selectedDepartment.lead_trade_leader)
                    ? selectedDepartment.lead_trade_leader
                    : [];

                  if (departmentLeads.length > 0) {
                    return (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          {departmentLeads.length === 1 ? 'Lead Trade Leader' : 'Lead Trade Leaders'}
                        </h3>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {departmentLeads.map(leadId => (
                              <span 
                                key={leadId}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                {getEmployeeName(leadId)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Department Analytics */}
                {(() => {
                  const deptAnalytics = analytics[selectedDepartment.id] || {};
                  if (deptAnalytics.activeEmployees !== undefined || deptAnalytics.recentHires !== undefined || deptAnalytics.avgTenure) {
                    return (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          Department Analytics
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-blue-600">{deptAnalytics.activeEmployees || 0}</p>
                            <p className="text-xs text-gray-500 mt-1">Active Employees</p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-green-600">{deptAnalytics.recentHires || 0}</p>
                            <p className="text-xs text-gray-500 mt-1">Recent Hires (3mo)</p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">
                              {deptAnalytics.avgTenure?.display || '0m'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Avg Tenure</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Employee List */}
                {(() => {
                  const deptEmployees = getDepartmentEmployees(selectedDepartment.department_name);
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        Department Employees ({deptEmployees.length})
                      </h3>
                      {deptEmployees.length > 0 ? (
                        <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto">
                          <div className="space-y-2">
                            {deptEmployees.map(employee => (
                              <div key={employee.id} className="flex items-center space-x-3 p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-medium text-blue-600">
                                    {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {employee.first_name} {employee.last_name}
                                  </p>
                                  <p className="text-xs text-gray-500">{employee.staff_id}</p>
                                </div>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                                  employee.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {employee.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-xl p-8 text-center">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">No employees assigned to this department</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Department"
        message="Are you sure you want to delete this department?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="admin"
      />
    </div>
  );
};

export default DepartmentManagement; 